import {observer} from 'mobx-react';
import React from 'react';
import {X1Language} from '../../../domain';
import {Link, PageHeading, PromiseMessage, PromiseRetry} from '../../../lib/components';
import {ReactNodeGenerator} from '../../../lib/helpers';
import {routes} from '../../../pages/Routes';
import {LanguageListStore} from './LanguageListStore';
import {AddLanguageControls} from './parts/AddLanguageControls';
import {AddLanguageForm} from './parts/AddLanguageForm';
import {DeleteLanguageControls} from './parts/DeleteLanguageControls';
import {EditLanguageControls} from './parts/EditLanguageControls';
import {EditLanguageForm} from './parts/EditLanguageForm';
import {ReorderLanguagesControls} from './parts/ReorderLanguagesControls';
import {userStatusService} from '../../../domain'

function LanguageViewNoActions(language: X1Language): React.ReactNode {
  return (
    <div className="row" key={'language-' + language.Id}>
      <div className="col">
        <div className="text-box">
          {language.Name}
        </div>
      </div>
    </div>
  );
}

function* ViewModeItems(store: LanguageListStore): ReactNodeGenerator {
  const languages = store.loader.languages;

  yield (
    <div className="row justify-content-end mb-1" key="view-actions">
      <div className="col-auto no-wrap">
        {
          userStatusService.status.roles.ContentManager &&
            <>
              <button type="button" className="btn btn-primary"
                      onClick={store.beginAdd}> Add
              </button>
              <button type="button" className="btn btn-primary"
                      disabled={languages.length < 2}
                      onClick={store.beginReorder}> Reorder
              </button>
            </>
        }
      </div>
    </div>);

  if (languages.length === 0)
    yield (
      <div className="row justify-content-center" key="no-languages">
        <div className="col-auto">
          <div className="alert alert-info">
            No Languages
          </div>
        </div>
      </div>);

  for (const language of languages) {
    yield (
      <div className="row" key={'language-' + language.Id}>
        <div className="col">
          <div className="text-box">
            {language.Name}
          </div>
        </div>
        <div className="col-auto no-wrap">
          <Link className="btn btn-primary" to={routes.x1language(language.Id)}> View </Link>
          {
            userStatusService.status.roles.ContentManager &&
            <>
              <button type="button" className="btn btn-primary"
                      onClick={() => store.beginEdit(language.Id)}> Edit
              </button>
              <button type="button" className="btn btn-danger"
                      onClick={() => store.beginDelete(language.Id)}> Delete
              </button>
            </>
          }
        </div>
      </div>);
  }
}

function* AddModeItems(store: LanguageListStore): ReactNodeGenerator {
  const languages = store.loader.languages;

  for (const language of languages)
    yield LanguageViewNoActions(language);

  yield (<AddLanguageForm key="add-language-form"
                          store={store}/>);

  yield (<AddLanguageControls key="add-language-controls"
                              store={store}/>);
}

function* EditModeItems(store: LanguageListStore): ReactNodeGenerator {
  const editLanguageId = store.editLanguage!.languageId;

  for (const language of store.loader.languages) {
    if (language.Id !== editLanguageId) {
      yield LanguageViewNoActions(language);
      continue;
    }

    yield (<EditLanguageForm key="edit-language-form"
                             store={store}/>);

    yield (<EditLanguageControls key="edit-language-controls"
                                 store={store}/>);
  }
}

function* DeleteModeItems(store: LanguageListStore): ReactNodeGenerator {
  const deleteLanguageId = store.deleteLanguage!.languageId;

  for (let language of store.loader.languages) {
    yield LanguageViewNoActions(language);

    if (language.Id !== deleteLanguageId) {
      continue;
    }

    yield (<DeleteLanguageControls key="delete-language-controls"
                                   store={store}/>);

  }
}

function* ReorderModeItems(store: LanguageListStore): ReactNodeGenerator {
  const reorder = store.reorder!;
  const languageIds = reorder.languageIds;

  for (let index = 0; index < languageIds.length; index++) {
    let languageId = languageIds[index];
    const language = store.loader.languageById[languageId];
    const first = index === 0;
    const last = index === languageIds.length - 1;

    yield (
      <div className="row" key={'language-' + language.Id}>
        <div className="col">
          <div className="text-box">
            {language.Name}
          </div>
        </div>
        <div className="col-auto no-wrap">
          <button type="button" className="btn btn-sm"
                  onClick={() => store.reorderUp(index)}
                  disabled={first}>
            <span className="fas fa-arrow-up"/>
          </button>
          <button type="button" className="btn btn-sm"
                  onClick={() => store.reorderDown(index)}
                  disabled={last}>
            <span className="fas fa-arrow-down"/>
          </button>
        </div>
      </div>);
  }

  yield (<ReorderLanguagesControls key="reorder-languages-controls"
                                   store={store}/>);
}

function* cardBodyRenderer(store: LanguageListStore): ReactNodeGenerator {
  const mode = store.mode;

  switch (mode) {
    case 'View':
      yield* ViewModeItems(store);
      break;
    case 'Edit':
      yield* EditModeItems(store);
      break;
    case 'Add':
      yield* AddModeItems(store);
      break;
    case 'Delete':
      yield* DeleteModeItems(store);
      break;
    case 'ReOrder':
      yield* ReorderModeItems(store);
      break;
  }
}

function* sceneRenderer(store: LanguageListStore): ReactNodeGenerator {
  const loader = store.loader;
  yield (<PageHeading key="heading">Languages</PageHeading>);

  yield (<PromiseMessage key="lookup-loader"
                         label="Loading language list"
                         state={loader.state} align="right"/>);

  yield (<PromiseRetry key="lookup-retry"
                       state={loader.state} retry={loader.load} align="right"/>);

  if (loader.state?.isResolved !== true)
    return;

  yield (
    <div className="row" key="main">
      <div className="col-12">
        <div className="card">
          <div className="card-header">
            Languages
          </div>

          <div className="card-body">
            {Array.from(cardBodyRenderer(store))}
          </div>
        </div>
      </div>
    </div>
  );
}

@observer
export class LanguageListComponent extends React.Component {
  store: LanguageListStore;

  constructor(props: any) {
    super(props);
    this.store = new LanguageListStore();
  }

  componentDidMount() {
    this.store.loader.load();
  }

  render(): React.ReactNodeArray {
    return Array.from(sceneRenderer(this.store));
  }

  componentWillUnmount(): void {
    this.store.dispose();
  }
}
